/**
 * Blocking tag lists (refund, exchange, store credit, non-returnable items,
 * refused orders) are compared strictly, case included, against the tags of
 * the order and of its products. A typo blocks nothing and shows nowhere:
 * Siamoise had `payedwithgiftcard` in its refund list while its orders carry
 * `paidwithgiftcard` (BBS-423). This flags the values that match no tag seen
 * on the shop, with the closest known tag when there is one.
 */
export type UnknownBlockingTag = {
  tag: string;
  suggestion?: string;
};

export type FindUnknownBlockingTagsOptions = {
  /**
   * Whether the known tags cover every tag in use. When they do not, a tag
   * missing from them proves nothing (it may sit on orders or products that
   * never got a return), so only the values close to a known tag are flagged:
   * those look like a typo whatever the coverage.
   */
  isComplete: boolean;
};

export function findUnknownBlockingTags(
  tags: string[],
  knownTags: ReadonlySet<string>,
  { isComplete }: FindUnknownBlockingTagsOptions,
): UnknownBlockingTag[] {
  if (knownTags.size === 0) return [];

  const unknownTags: UnknownBlockingTag[] = [];
  for (const tag of new Set(tags)) {
    if (knownTags.has(tag)) continue;
    const suggestion = findClosestTag(tag, knownTags);
    if (suggestion) unknownTags.push({ tag, suggestion });
    else if (isComplete) unknownTags.push({ tag });
  }
  return unknownTags;
}

function normalize(tag: string): string {
  return tag.trim().toLowerCase();
}

function findClosestTag(
  tag: string,
  knownTags: ReadonlySet<string>,
): string | undefined {
  const normalized = normalize(tag);
  for (const known of knownTags) {
    if (normalize(known) === normalized) return known;
  }

  // Two edits catch payed/paid; on short tags they would match anything.
  const maxDistance = normalized.length >= 6 ? 2 : 1;
  let closest: string | undefined;
  let closestDistance = maxDistance + 1;
  for (const known of knownTags) {
    const distance = boundedLevenshtein(
      normalized,
      normalize(known),
      closestDistance - 1,
    );
    if (distance < closestDistance) {
      closest = known;
      closestDistance = distance;
    }
  }
  return closest;
}

/** Edit distance, or `max + 1` as soon as it is known to exceed `max`. */
function boundedLevenshtein(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;

  let previous = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const current = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      current[j] = Math.min(
        previous[j] + 1,
        current[j - 1] + 1,
        previous[j - 1] + cost,
      );
      rowMin = Math.min(rowMin, current[j]);
    }
    if (rowMin > max) return max + 1;
    previous = current;
  }
  return previous[b.length];
}
